/**
 * Design Tokens
 * Centralized design system for maintainability
 *
 * Aesthetic: "Command Center" - Professional, technical, data-dense
 * Fonts: IBM Plex Sans (UI) + JetBrains Mono (data)
 */

// Color Palette
export const colors = {
  // Brand colors - Professional blue with cyan accent
  brand: {
    50: '#E6F4FF',
    100: '#BAE3FF',
    200: '#7CC4FA',
    300: '#47A3F3',
    400: '#2186EB',
    500: '#0967D2',  // Primary
    600: '#0552B5',
    700: '#03449E',
    800: '#01337D',
    900: '#002159',
  },

  // Accent - Distinctive cyan for highlights
  accent: {
    light: '#0EA5E9',  // Sharper cyan for light mode
    dark: '#22D3EE',   // Brighter cyan for dark mode
  },

  // Severity colors - Datadog style palette
  severity: {
    critical: '#632CA6',  // Purple
    high: '#DA291C',      // Red
    medium: '#FFCF00',    // Yellow
    low: '#2D8B2D',       // Green
  },

  // Chakra colorScheme names for badges (must match severity keys)
  severityColorScheme: {
    critical: 'purple',
    high: 'red',
    medium: 'yellow',
    low: 'green',
  } as Record<string, string>,

  // Chart colors
  chart: {
    primary: '#0EA5E9',   // Cyan
    secondary: '#8B5CF6', // Purple
    tertiary: '#EC4899',  // Pink
    total: '#3182CE',     // Blue for aggregate/total bars
    background: {
      light: '#FFFFFF',
      dark: '#1A1F26',
    },
  },

  // Status colors
  status: {
    active: '#16A34A',
    inactive: '#64748B',
    warning: '#CA8A04',
    error: '#DC2626',
    info: '#0EA5E9',
  },

  // UI colors - Refined palette
  ui: {
    background: {
      light: '#F8FAFC',   // Cooler white
      dark: '#0F1419',    // Rich dark (like GitHub dark)
    },
    card: {
      light: '#FFFFFF',
      dark: '#1A1F26',    // Slightly lighter than bg
    },
    tableStripe: {
      light: '#F1F5F9',   // Subtle gray for alternating rows
      dark: '#1E252E',    // Slightly different from card bg
    },
    cardHover: {
      light: '#F1F5F9',
      dark: '#242A33',
    },
    border: {
      light: '#E2E8F0',
      dark: '#2D3748',
    },
    borderSubtle: {
      light: '#F1F5F9',
      dark: '#1E252E',
    },
    text: {
      primary: {
        light: '#0F172A',  // Darker for better contrast
        dark: '#F1F5F9',
      },
      secondary: {
        light: '#64748B',
        dark: '#94A3B8',
      },
      muted: {
        light: '#94A3B8',
        dark: '#64748B',
      },
    },
  },

  // Filter state colors
  filter: {
    manual: {
      active: '#718096',
      inactive: '#64748B',
    },
    ai: {
      active: '#0891B2',  // Cyan - distinct from purple (now used for critical)
      inactive: '#64748B',
    },
    impact: {
      background: {
        light: '#E0F2FE',
        dark: '#0C4A6E',
      },
      bar: '#0EA5E9',
    },
  },
} as const

// Typography - IBM Plex family for technical, professional feel
export const typography = {
  fonts: {
    heading: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    body: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono: '"IBM Plex Mono", "JetBrains Mono", SFMono-Regular, Menlo, monospace',
  },

  fontSizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    md: '1rem',       // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
  },

  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  lineHeights: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
} as const

// Spacing
export const spacing = {
  0: '0',
  1: '0.25rem',   // 4px
  2: '0.5rem',    // 8px
  3: '0.75rem',   // 12px
  4: '1rem',      // 16px
  5: '1.25rem',   // 20px
  6: '1.5rem',    // 24px
  8: '2rem',      // 32px
  10: '2.5rem',   // 40px
  12: '3rem',     // 48px
  16: '4rem',     // 64px
  20: '5rem',     // 80px
} as const

// Border radius
export const radii = {
  none: '0',
  sm: '0.125rem',   // 2px
  base: '0.25rem',  // 4px
  md: '0.375rem',   // 6px
  lg: '0.5rem',     // 8px
  xl: '0.75rem',    // 12px
  full: '9999px',
} as const

// Shadows - Refined for both modes
export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  // Dark mode specific - subtle glow effect
  dark: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    card: '0 0 0 1px rgba(255, 255, 255, 0.05), 0 4px 12px rgba(0, 0, 0, 0.4)',
    glow: '0 0 20px rgba(14, 165, 233, 0.15)',  // Cyan glow
  },
} as const

// Transitions
export const transitions = {
  duration: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
  },
  easing: {
    ease: 'ease',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out',
  },
} as const

// Z-index
export const zIndices = {
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  modal: 1300,
  popover: 1400,
  tooltip: 1500,
} as const

// Breakpoints
export const breakpoints = {
  sm: '30em',   // 480px
  md: '48em',   // 768px
  lg: '62em',   // 992px
  xl: '80em',   // 1280px
  '2xl': '96em', // 1536px
} as const

// Component-specific tokens
export const components = {
  chart: {
    title: {
      fontSize: typography.fontSizes.sm,
      fontWeight: typography.fontWeights.semibold,
    },
    height: {
      sm: '200px',
      md: '280px',
      lg: '400px',
    },
    barRadius: 4,
  },
  table: {
    rowHeight: '48px',
    headerHeight: '40px',
  },
  filter: {
    pillHeight: '24px',
    inputHeight: '32px',
  },
} as const
